import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { Loader } from '../../components/Loader/Loader';
import { createKeycloakConnection } from './initiateKeycloak';
import { useLoginApi } from '../../apiHooks/apiHooks';
import { AppContextData } from '../AppContext';
import { domain } from '../../handleCookies/index';

const storeKeycloakAuthConfig = (authType, orgId, keycloak) => {
  const cookieOpts = { domain, secure: true };
  Cookies.set('zinierkeycloakurl', authType.provider, cookieOpts);
  Cookies.set('zinierkeycloakrealm', orgId, cookieOpts);
  Cookies.set('zinierkeycloakclientid', authType.clientId, cookieOpts);
  Cookies.set('zinierkeycloakidtoken', keycloak.idToken || '', cookieOpts);
  Cookies.set('zinierkeycloakaccesstoken', keycloak.token, cookieOpts);
  if (keycloak.refreshToken) {
    Cookies.set('zinierkeycloakrefreshtoken', keycloak.refreshToken, cookieOpts);
  }
};

export const KeycloakProviderProxy = (keycloakProps) => {
  const { handleKeycloakLoginApi } = useLoginApi();
  const { state } = useContext(AppContextData);
  const [loading, setLoading] = useState(true);
  const kcFlowStartedRef = useRef(false);

  const authenticateToken = useCallback(
    async (keycloak) => {
      const token = keycloak.token;
      const loginId =
        keycloak.tokenParsed?.preferred_username ||
        keycloak.tokenParsed?.email ||
        '';
      await handleKeycloakLoginApi(state, token, loginId);
    },
    [handleKeycloakLoginApi, state]
  );

  useEffect(() => {
    if (kcFlowStartedRef.current) {
      setLoading(false);
      return undefined;
    }
    kcFlowStartedRef.current = true;

    let isCancelled = false;

    // eslint-disable-next-line max-statements
    (async function initiateKeycloakLogin () {
      let keycloak;
      try {
        keycloak = createKeycloakConnection(keycloakProps.authType, state.org_id);

        const authenticated = await keycloak.init({
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false
        });

        if (authenticated) {
          storeKeycloakAuthConfig(keycloakProps.authType, state.org_id, keycloak);
          await authenticateToken(keycloak);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Keycloak initialization error:', e);
        if (!isCancelled) {
          const encodedMessage = encodeURIComponent(
            e?.message || 'Keycloak initialization failed'
          );
          window.location.href =
            window.location.origin +
            `/error?code=KEYCLOAK_INIT_ERROR&message=${encodedMessage}`;
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isCancelled = true;
      // Do NOT reset kcFlowStartedRef here: this effect's deps (keycloakProps,
      // authenticateToken) are new object/function references on every re-render
      // (authenticateToken's useCallback depends on the whole context `state` object,
      // which changes identity on every dispatch — including the ones this very flow
      // triggers, e.g. SET_LOADER/LOGIN_SUCCESS). Resetting here reran keycloak.init()
      // — and its login-required redirect — on every such re-render, causing a refresh
      // loop in production. A real remount gets a fresh useRef(false) automatically,
      // so no manual reset is needed for that case either.
    };
  }, [keycloakProps, authenticateToken, state.org_id]);

  return <div>{loading ? <Loader type="fullScreen" /> : null}</div>;
};

export const KeycloakProvider = KeycloakProviderProxy;
